import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { JobView, RemovalDetail } from '@/api/opsTypes';
import { getSession } from '@/auth/session';
import { StatusChip } from '@/components/ops/Badges';
import { ReasonField, TextField, reasonOk } from '@/components/ops/Forms';
import { KeyValues, Section } from '@/components/ops/Layout';
import { ConfirmDialog } from '@/components/ops/Modal';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatBytes, formatTimestamp } from '@/ops/format';
import { formatWhen } from '@/pages/AccountsPage';

/** True while a deletion can still be stopped: its registry step has not started. */
export function canStopDeletion(job: Pick<JobView, 'op' | 'status' | 'steps'>): boolean {
  if (job.op !== 'delete' || job.status === 'done' || job.status === 'done_with_warnings' || job.status === 'cancelled') {
    return false;
  }

  const registry = (job.steps ?? []).find((s) => s.key === 'delete.purge_registry');
  return !registry || registry.status === 'pending';
}

/** What the backup card says about the host runner's task. */
export function backupWords(backup: RemovalDetail['backup']): string {
  if (!backup) return 'Not started yet — it starts once the tenant is frozen.';
  switch (backup.status) {
    case 'queued': return 'Queued for the server — waiting for the host runner to pick it up.';
    case 'running': return `The server is backing the tenant up${backup.runner ? ` (${backup.runner})` : ''}.`;
    case 'done': return backup.result?.restore_ok ? 'Backed up, and the backup was restored successfully as a test.' : 'Finished, but without a passing restore test.';
    case 'failed': return `Failed: ${backup.error ?? 'no detail'}.`;
    default: return backup.status;
  }
}

/**
 * The deletion's decisions (plan hazy-tumbling-graham Stage 2): who asked and why, the second owner's approval, the
 * verified backup, the typed confirmation — and Stop (reject), which brings a frozen tenant back online. A discard shows
 * where it came from (the cancelled setup) and needs no decision.
 */
export function RemovalPanel({ job }: { job: JobView }) {
  const queryClient = useQueryClient();
  const running = job.status === 'pending' || job.status === 'running';
  const removal = useQuery({
    queryKey: ['ops', 'removal', job.id],
    queryFn: () => opsApi.provisioning.removal(job.id),
    refetchInterval: running ? 5_000 : false,
  });
  const me = getSession()?.user.id ?? null;
  const [typed, setTyped] = useState('');
  const [stopping, setStopping] = useState(false);
  const [reason, setReason] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['ops', 'removal', job.id] });
    queryClient.invalidateQueries({ queryKey: ['ops', 'job', job.id] });
  };
  const approve = useMutation({ mutationFn: () => opsApi.provisioning.approveRemoval(job.id), onSuccess: refresh });
  const confirm = useMutation({ mutationFn: () => opsApi.provisioning.confirmRemoval(job.id, typed.trim()), onSuccess: () => { setTyped(''); refresh(); } });
  const reject = useMutation({
    mutationFn: () => opsApi.provisioning.rejectRemoval(job.id, reason.trim()),
    onSuccess: () => { setStopping(false); setReason(''); refresh(); },
  });

  return (
    <Section title={job.op === 'discard' ? 'Clean-up of a cancelled setup' : 'Deletion'} meta={job.op === 'discard' ? 'no approval or backup' : 'two owners, a verified backup, a typed confirmation'}>
      <QueryState query={removal}>
        {(r) => (
          <div className="stack-sm" data-testid="removal-panel">
            <KeyValues
              rows={[
                { label: 'Asked by', value: <>{r.requested_by_name} <span className="muted">· {formatWhen(job.created_at)}</span></> },
                { label: 'Why', value: r.reason ?? '—' },
                ...(r.source_job_id
                  ? [{ label: 'Cancelled setup', value: <Link to={`/ops/provisioning/jobs/${r.source_job_id}`}>open the setup job</Link> }]
                  : []),
                ...r.approvals.map((a) => ({
                  label: a.type === 'approve' ? 'Approved by' : a.type === 'confirm' ? 'Confirmed by' : 'Stopped by',
                  value: <>{a.actor_name} <span className="muted">· {formatTimestamp(a.at)}</span>{a.note ? <span className="muted"> — {a.note}</span> : null}</>,
                })),
              ]}
            />

            {r.waiting_for === 'approve' && (
              r.requested_by_id === me ? (
                <p className="t-body note-warning" style={{ margin: 0 }} data-testid="removal-needs-other-owner">
                  Waiting for <strong>another owner</strong> to approve — you asked for this deletion, so you cannot approve it. Send them
                  this page.
                </p>
              ) : (
                <div className="row">
                  <button type="button" className="btn btn-primary" disabled={approve.isPending} onClick={() => approve.mutate()} data-testid="approve-removal">
                    {approve.isPending ? 'Approving…' : 'Approve the deletion'}
                  </button>
                  <span className="t-caption muted">The tenant is then frozen and backed up. Nothing is removed until an owner confirms.</span>
                </div>
              )
            )}
            <MutationError error={approve.error} />

            {job.op === 'delete' && (
              <div className="card card-body stack-sm" data-testid="backup-card">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong className="t-heading">Backup</strong>
                  {r.backup && <StatusChip status={r.backup.status} tone={r.backup.status === 'failed' ? 'danger' : r.backup.status === 'done' ? 'success' : 'muted'} />}
                </div>
                <span className="t-body">{backupWords(r.backup)}</span>
                {r.backup?.result && (
                  <KeyValues
                    rows={[
                      { label: 'Databases', value: <span className="mono">{(r.backup.result.databases ?? []).join(', ') || '—'}</span> },
                      { label: 'Size', value: formatBytes(r.backup.result.backup_bytes ?? null) },
                      { label: 'Where', value: <span className="mono wrap">{r.backup.result.path ?? r.backup.result.backup_ref ?? '—'}</span> },
                      ...(r.backup.finished_at ? [{ label: 'Finished', value: formatTimestamp(r.backup.finished_at) }] : []),
                    ]}
                  />
                )}
              </div>
            )}

            {r.waiting_for === 'confirm' && (
              <div className="card card-body stack-sm callout-error" data-testid="confirm-removal">
                <strong className="t-heading">Confirm the deletion</strong>
                <span className="t-body">
                  The backup is verified. Type <span className="mono"><strong>{r.db_short}</strong></span> to remove the tenant for good. Its
                  databases are dropped and cannot be brought back except from the backup above.
                </span>
                <TextField label="Tenant short name" value={typed} onChange={setTyped} mono autoComplete="off" />
                <div className="row">
                  <button type="button" className="btn btn-danger" disabled={typed.trim() !== r.db_short || confirm.isPending}
                    onClick={() => confirm.mutate()}>
                    {confirm.isPending ? 'Confirming…' : 'Delete the tenant'}
                  </button>
                </div>
                <MutationError error={confirm.error} />
              </div>
            )}

            {canStopDeletion(job) && (
              <div>
                <button type="button" className="btn btn-ghost is-danger" onClick={() => { reject.reset(); setStopping(true); }} data-testid="stop-removal">
                  Stop the deletion
                </button>
              </div>
            )}

            {stopping && (
              <ConfirmDialog title="Stop this deletion?" confirmLabel="Stop the deletion" pending={reject.isPending}
                confirmDisabled={!reasonOk(reason)} onConfirm={() => reject.mutate()}
                onCancel={() => { setStopping(false); reject.reset(); }}>
                <p className="t-body" style={{ margin: 0 }}>
                  If the tenant is frozen it is unfrozen first: sign-in and its web addresses work again and its subscription is restored.
                  The job is then cancelled. A backup already taken stays on the server.
                </p>
                <ReasonField value={reason} onChange={setReason} />
                <MutationError error={reject.error} />
              </ConfirmDialog>
            )}
          </div>
        )}
      </QueryState>
    </Section>
  );
}
